import { create } from 'zustand';
import { IChatMessage, ChatContextType } from '@packages/global';

/**
 * 聊天消息缓存 Store
 *
 * 按 contextKey (projectId + contextType) 缓存消息，
 * 避免步骤切换时重复请求 /api/user/chat-messages
 */

interface CacheEntry {
  messages: IChatMessage[];
  loadedAt: number; // 加载时间戳
}

interface ChatMessageCacheState {
  cache: Record<string, CacheEntry>;

  /** 获取缓存的消息，如果存在且未过期则返回，否则返回 null */
  getCached: (projectId: string, contextType: ChatContextType) => IChatMessage[] | null;

  /** 写入缓存 */
  setCache: (projectId: string, contextType: ChatContextType, messages: IChatMessage[]) => void;

  /** 追加消息到缓存（用于本地新增消息） */
  appendMessage: (projectId: string, contextType: ChatContextType, message: IChatMessage) => void;

  /** 从缓存中删除单条消息 */
  removeMessage: (projectId: string, contextType: ChatContextType, messageId: string) => void;

  /** 清空某个上下文的缓存 */
  clearCache: (projectId: string, contextType: ChatContextType) => void;

  /** 清空所有缓存 */
  clearAll: () => void;
}

function buildKey(projectId: string, contextType: ChatContextType): string {
  return `${projectId}::${contextType}`;
}

/** 缓存有效期：10 分钟 */
const CACHE_TTL = 10 * 60 * 1000;

export const useChatMessageCacheStore = create<ChatMessageCacheState>((set, get) => ({
  cache: {},

  getCached: (projectId, contextType) => {
    const key = buildKey(projectId, contextType);
    const entry = get().cache[key];
    if (!entry) return null;
    if (Date.now() - entry.loadedAt > CACHE_TTL) return null;
    return entry.messages;
  },

  setCache: (projectId, contextType, messages) => {
    const key = buildKey(projectId, contextType);
    set(state => ({
      cache: {
        ...state.cache,
        [key]: { messages, loadedAt: Date.now() }
      }
    }));
  },

  appendMessage: (projectId, contextType, message) => {
    const key = buildKey(projectId, contextType);
    set(state => {
      const entry = state.cache[key];
      if (!entry) return state;
      return {
        cache: {
          ...state.cache,
          [key]: { ...entry, messages: [...entry.messages, message] }
        }
      };
    });
  },

  removeMessage: (projectId, contextType, messageId) => {
    const key = buildKey(projectId, contextType);
    set(state => {
      const entry = state.cache[key];
      if (!entry) return state;
      return {
        cache: {
          ...state.cache,
          [key]: { ...entry, messages: entry.messages.filter(m => m.id !== messageId) }
        }
      };
    });
  },

  clearCache: (projectId, contextType) => {
    const key = buildKey(projectId, contextType);
    set(state => {
      const newCache = { ...state.cache };
      delete newCache[key];
      return { cache: newCache };
    });
  },

  clearAll: () => set({ cache: {} }),
}));
